import * as React from 'react';
import ReactQuill from 'react-quill';

import {useMutation} from '@apollo/react-hooks';
import classNames from 'classnames';

import {isRichTextareaEmpty} from 'shells/rich-textarea/helpers';

import type {
    LogActivity as LogActivityMutation,
    LogActivityVariables as LogActivityMutationVariables,
    CreateNote as CreateNoteMutation,
    CreateNoteVariables as CreateNoteMutationVariables,
    GetTeams_teams as Team,
} from 'nutshell-graphql-types';

import {TextareaMentionable} from '../../core/textarea-mentionable/textarea-mentionable';
import {refetchForEvent} from '../../lead/refetch-helpers';
import {accountRefetchForEvent} from '../../peep/account/hooks/use-account-update-refetch';
import {contactRefetchForEvent} from '../../peep/contact/hooks/use-contact-update-refetch';
import {doesKeyCancelTranscribe, mutateQuillHtmlValueForTranscribing} from '../../speech-to-text';

import CREATE_NOTE from './graphql/mutations/create-note.graphql';
import LOG_ACTIVITY from './graphql/mutations/log-activity.graphql';
import type {ContentType} from './timeline-creator';
import {TimelineCreatorFooterBar} from './timeline-creator-footer-bar';

import styles from './timeline-creator.module.css';

export type ActivityFormData = {
    activityTypeId: string | null | undefined;
    durationMinutes: number;
};

export type NoteFormData = {
    pinNote: boolean;
};

type SnapshottedValues = {
    htmlValue: string;
    plaintextValue: string;
};

export type LocationVariant = 'timeline' | 'lead-page';

export type StateProps = {
    htmlValue: string;
    plaintextValue: string;
    onChange: (htmlValue: string, plaintextValue: string) => void;
    activityTypeId: string | null | undefined;
    setActivityTypeId: (arg1?: string | null | undefined) => void;
    durationMinutes: number;
    setDurationMinutes: (arg1: number) => void;
    pinNote: boolean;
    setPinNote: (arg1: boolean) => void;
    isTranscribing: boolean;
    setIsTranscribing: (arg1: boolean) => void;
    onResetAfterSubmit?: () => void;
};

export type OwnProps = {
    contentType: ContentType;
    entityId: string;
    entityName: string;
    refetchEvents: () => Promise<any>;
    shouldAutoFocusEditor: boolean;
    variant?: LocationVariant;
    preloadedTeams?: Team[] | null | undefined;
    onClose?: () => void;
    onAfterSubmit?: (id: string, activityTypeName?: string, noteBody?: string) => void;
    isFullWidth?: boolean;
    setHasNoteValue?: (hasValue: boolean) => void;
    setHasActivityFormDataChanged?: (hasChanged: boolean) => void;
    onFocus?: () => void;
};

type Props = OwnProps & StateProps;

export function TimelineCreatorForm(props: Props) {
    const {
        contentType,
        entityId,
        entityName,
        refetchEvents,
        shouldAutoFocusEditor,
        variant = 'timeline',
        isFullWidth = false,
        setHasNoteValue,
        htmlValue,
        plaintextValue,
        onChange,
        onFocus,
        activityTypeId,
        setActivityTypeId,
        durationMinutes,
        setDurationMinutes,
        pinNote,
        setPinNote,
        isTranscribing,
        setIsTranscribing,
        onResetAfterSubmit,
    } = props;

    const quillRef = React.useRef<ReactQuill | null | undefined>(null);
    const [snapshottedValues, setSnapshottedValues] = React.useState<
        SnapshottedValues | null | undefined
    >(null);
    const [mostRecentStreamContent, setMostRecentStreamContent] = React.useState<string>('');
    const [fullStreamContent, setFullStreamContent] = React.useState<string>('');
    const [interimResult, setInterimResult] = React.useState<string>('');

    const submitButtonRef = React.useRef(undefined);

    const isActivity = contentType === 'ACTIVITY';
    const isNote = contentType === 'NOTE';
    const isInputEmpty = isRichTextareaEmpty(htmlValue, plaintextValue);

    const stopTranscribeRef = React.useRef(() => {});
    const wasTranscribingRef = React.useRef(isTranscribing);

    // When isTranscribing changes to false externally (e.g., another instance
    // of this component mounts and resets the state), we need to call the
    // actual stopTranscribe function to properly reset the notetaker UI
    React.useEffect(() => {
        if (wasTranscribingRef.current && !isTranscribing) {
            stopTranscribeRef.current();
        }
        wasTranscribingRef.current = isTranscribing;
    }, [isTranscribing]);

    // Log activity mutation
    const [logActivity, {loading: isLoadingNote}] = useMutation<
        LogActivityMutation,
        LogActivityMutationVariables
    >(LOG_ACTIVITY);

    // Create note mutation
    const [createNote, {loading: isLoadingActivity}] = useMutation<
        CreateNoteMutation,
        CreateNoteMutationVariables
    >(CREATE_NOTE);

    const moveCursorToEndOfCurrentTranscription = (quillEditor: any) => {
        quillEditor.setSelection(quillEditor.getLength(), 0);
    };

    React.useEffect(() => {
        if (isTranscribing && htmlValue.length && quillRef && quillRef.current) {
            moveCursorToEndOfCurrentTranscription(quillRef.current.getEditor());
        }
    }, [htmlValue, quillRef, isTranscribing]);

    // Update snapshotted values when transcription starts/stops
    React.useEffect(() => {
        if (isTranscribing && snapshottedValues === null) {
            setSnapshottedValues({
                htmlValue: mutateQuillHtmlValueForTranscribing(htmlValue),
                plaintextValue: plaintextValue,
            });
        } else if (!isTranscribing && snapshottedValues !== null) {
            setSnapshottedValues(null);
        }
    }, [snapshottedValues, htmlValue, plaintextValue, isTranscribing]);

    // Update the editor value when transcription stream updates.
    React.useEffect(() => {
        if (mostRecentStreamContent.length) {
            // This will exist, but flow doesn't know that
            if (snapshottedValues) {
                onChange(
                    `${snapshottedValues.htmlValue} ${fullStreamContent} ${mostRecentStreamContent}`,
                    `${snapshottedValues.plaintextValue} ${fullStreamContent} ${mostRecentStreamContent}`
                );
            }

            setFullStreamContent(`${fullStreamContent} ${mostRecentStreamContent}`);
            setMostRecentStreamContent('');
            setInterimResult('');
        }
    }, [
        snapshottedValues,
        mostRecentStreamContent,
        htmlValue,
        plaintextValue,
        fullStreamContent,
        quillRef,
        onChange,
    ]);

    React.useEffect(() => {
        if (interimResult.length && snapshottedValues) {
            onChange(
                `${snapshottedValues.htmlValue} ${fullStreamContent} ${interimResult}`,
                `${snapshottedValues.plaintextValue} ${fullStreamContent} ${interimResult}`
            );
        }
    }, [interimResult, quillRef, fullStreamContent, snapshottedValues, onChange]);

    React.useEffect(() => {
        if (!isTranscribing && fullStreamContent.length) {
            setFullStreamContent('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTranscribing]);

    React.useEffect(() => {
        if (setHasNoteValue) {
            setHasNoteValue(!isInputEmpty);
        }
    }, [isInputEmpty, setHasNoteValue]);

    // On form submit
    const onSubmit = (
        e: React.SyntheticEvent,
        activityFormData?: ActivityFormData | null,
        noteFormData?: NoteFormData | null
    ): Promise<any> => {
        e.preventDefault();

        // Prevent submitting empty notes
        if (!isActivity && isInputEmpty) return Promise.reject();

        if (isActivity && activityFormData) {
            return logActivity({
                variables: {
                    input: {
                        activity: {
                            participants: entityName === 'Leads' ? [] : [entityId],
                            leadIds: entityName === 'Leads' ? [entityId] : [],
                            logNote: plaintextValue,
                            activityTypeId: activityFormData.activityTypeId,
                            durationMinutes: activityFormData.durationMinutes,
                        },
                    },
                },
            }).then((result) => {
                const id = result.data?.logActivity?.activity?.id ?? '';
                const activityTypeName = result.data?.logActivity?.activity?.activityType?.name;
                return Promise.resolve(refetchEvents()).then(() => {
                    if (entityName === 'Leads') {
                        refetchForEvent('activityStatusChanged');
                    } else if (entityName === 'Contacts') {
                        contactRefetchForEvent('activityLogged');
                    } else if (entityName === 'Accounts') {
                        accountRefetchForEvent('activityLogged');
                    }
                    props.onAfterSubmit?.(id, activityTypeName);
                    clearEditorInput();
                });
            });
        } else {
            // Create note
            return createNote({
                variables: {
                    input: {
                        note: {
                            value: plaintextValue,
                            entityId,
                            isPinned: noteFormData && noteFormData.pinNote,
                        },
                    },
                },
            }).then((result) => {
                const id = result.data?.noteCreate?.note?.id ?? '';
                const noteBody = plaintextValue;
                clearEditorInput();
                return Promise.resolve(refetchEvents()).then(() => {
                    if (NutClientConfig.hasLeadPageRedesignM3FF) {
                        refetchForEvent('noteCreated');
                    }
                    props.onAfterSubmit?.(id, undefined, noteBody);
                });
            });
        }
    };

    // Submit when user presses command + enter
    // @ts-expect-error
    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            if (submitButtonRef && submitButtonRef.current) {
                // Simulate clicking the submit button when key combo is entered
                // @ts-expect-error
                submitButtonRef.current.click();
            }
        }
    };

    const clearEditorInput = () => {
        onChange('', '');
    };

    const textAreaContainerStyles = classNames(styles['text-area-container'], {
        [styles['text-area-container--lead-page']]: variant === 'lead-page',
    });

    return (
        <form onKeyDown={handleKeyDown} className={styles['content-container']}>
            <div className={textAreaContainerStyles}>
                <TextareaMentionable
                    id='timelineCreatorEditor'
                    getQuillRef={(ref: HTMLElement | null | undefined) => {
                        quillRef.current = ref as ReactQuill | null | undefined;
                    }}
                    isFullHeight={true}
                    isFullWidth={isFullWidth}
                    widePadding={true}
                    overflowVisible={true}
                    placeholder={
                        isActivity
                            ? 'Log what happened in your activity… @Mention other users to grab their attention, or reference other companies, people, or leads.'
                            : 'Write a note… @Mention other users to grab their attention, or reference other companies, people, and leads.'
                    }
                    largerText={true}
                    value={htmlValue}
                    hasTranscription={true}
                    onChange={onChange}
                    onFocus={onFocus}
                    onKeyPress={() => {
                        stopTranscribeRef.current();
                    }}
                    // copied from body-field.js
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (doesKeyCancelTranscribe(e.key)) {
                            stopTranscribeRef.current();
                        }
                    }}
                    onKeyUp={(e: React.KeyboardEvent) => {
                        if (doesKeyCancelTranscribe(e.key)) {
                            stopTranscribeRef.current();
                        }
                    }}
                    onTabPropagateToQuill={true}
                    forceFocus={shouldAutoFocusEditor}
                    autoFocus={shouldAutoFocusEditor}
                    includeNotetaker={NutClientConfig.enableNotetaker}
                    notetakerProps={{
                        onStartTranscribe: () => {
                            setIsTranscribing(true);
                        },
                        onEndTranscribe: () => {
                            setIsTranscribing(false);
                        },
                        handleTranscribe: (text, isEndpoint) => {
                            if (isEndpoint) {
                                setMostRecentStreamContent(text);
                            } else {
                                setInterimResult(text);
                            }
                        },
                        stopTranscribe: (stopTranscribe) => {
                            stopTranscribeRef.current = stopTranscribe;
                        },
                        location: 'timeline',
                    }}
                    preloadedTeams={variant === 'lead-page' ? props.preloadedTeams : undefined}
                    hasBorder={variant === 'lead-page'}
                    styleVariant={variant === 'lead-page' && isNote ? 'notes' : undefined}
                />
            </div>
            <TimelineCreatorFooterBar
                contentType={contentType}
                entityId={entityId}
                entityName={entityName}
                plaintextValue={plaintextValue}
                clearEditorInput={clearEditorInput}
                onSubmit={onSubmit}
                isLoading={isLoadingActivity || isLoadingNote}
                isDisabled={!isActivity && isInputEmpty}
                // @ts-expect-error
                getSubmitButtonRef={(ref) => {
                    if (ref) {
                        submitButtonRef.current = ref;
                    }
                }}
                variant={variant}
                onClose={props.onClose}
                setHasActivityFormDataChanged={props.setHasActivityFormDataChanged}
                activityTypeId={activityTypeId}
                setActivityTypeId={setActivityTypeId}
                durationMinutes={durationMinutes}
                setDurationMinutes={setDurationMinutes}
                pinNote={pinNote}
                setPinNote={setPinNote}
                onResetAfterSubmit={onResetAfterSubmit}
            />
        </form>
    );
}
